import imageCompression from 'browser-image-compression';
import type { UploadImageResponse } from '$shared/api';
import { VITE_API_BASE_URL } from '$lib/env';

export class ImageUploader {
  private maxSizeMB: number;
  private apiUrl: string;

  constructor(maxSizeMB = 1) {
    this.maxSizeMB = maxSizeMB;
    this.apiUrl = `${VITE_API_BASE_URL}/api/upload-image`;
  }

  async compressImage(file: File): Promise<File> {
    // Skip compression for GIFs (If we attempt to compress them, they will become static)
    // Other option is to bundle ffmpeg in the frontend which is a very large dependency
    if (file.type === 'image/gif') {
      return file;
    }

    if (file.size / 1024 / 1024 > this.maxSizeMB) {
      const compressedFile = await imageCompression(file, {
        maxSizeMB: this.maxSizeMB,
        maxWidthOrHeight: 400,
        useWebWorker: true,
        maxIteration: 6,
      });

      const compressedFileWithOriginalName = new File(
        [compressedFile],
        file.name,
        {
          type: compressedFile.type,
          lastModified: new Date().getTime(),
        },
      );

      // biome-ignore lint/style/noParameterAssign: TODO: Don't reassign parameter
      file = compressedFileWithOriginalName;
    }

    if (file.size / 1024 / 1024 > this.maxSizeMB) {
      throw new Error(
        `Files cannot exceed ${this.maxSizeMB}MB total in size after compression.`,
      );
    }

    return file;
  }

  uploadImage(
    file: File,
    onProgress?: (percentComplete: number) => void,
  ): Promise<UploadImageResponse> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('image', file);
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable && onProgress) {
          const percentComplete = Number((evt.loaded / evt.total) * 100);
          onProgress(percentComplete);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const response: UploadImageResponse = JSON.parse(xhr.response);
          resolve(response);
        } else {
          reject(new Error('Upload failed'));
        }
      };

      xhr.onerror = () => {
        reject(new Error('Upload failed'));
      };

      xhr.open('POST', this.apiUrl, true);
      xhr.send(formData);
    });
  }

  async compressAndUpload(
    file: File,
    onProgress?: (percentComplete: number) => void,
  ): Promise<UploadImageResponse> {
    const compressedImage = await this.compressImage(file);
    return this.uploadImage(compressedImage, onProgress);
  }
}
